import React from 'react';
import {
  ActivityAssignmentData,
  StepWithResponseData,
} from 'hooks/activities/useQueryActivityAssignments';
import { Grid, Paper, Typography } from '@mui/material';
import SectionHeader from 'components/SectionHeader/SectionHeader';
import { Tag } from 'components/Tag/Tag';
import { themeOptions } from 'theme';
import { Div } from 'components/Elements/Div/Div';
import OverviewDetail from 'components/Overview/OverviewDetail/OverviewDetail';
import CompleteAssignmentStep from 'pages/Activities/Steps/CompleteAssignmentStep';
import CustomIcon from 'components/Icon/CustomIcon';
import { dateFormatter, formatDatePickerDate, formatMinutes, getUsersName } from 'utils/formatting';
import CheckIcon from '@mui/icons-material/Check';
import useActivityAssignmentStepDetails from 'pages/Activities/ActivityAssignmentStep/useActivityAssignmentStepDetails';
import RowDetail from 'components/Overview/RowDetails/RowDetail';
import MediaGallery from 'components/MediaGallery/MediaGallery';
import StepQuestion from 'pages/Activities/ActivityAssignmentStep/StepQuestion/StepQuestion';
import { Box } from '@mui/system';
import Stack from '@mui/material/Stack';
import DropdownMenu from 'components/DropdownMenu/DropdownMenu';
import CommentOnAssignmentStep from 'pages/Activities/ActivityAssignmentStep/CommentOnAssignmentStep';
import { useAuthContext } from 'context/auth.context';
import { hasPermissions } from 'config/ability';

interface ActivityAssignmentStepProps {
  activityAssignment: ActivityAssignmentData;
  stepWithResponse: StepWithResponseData;
  stepNumber: number;
  totalSteps: number;
  hideComplete?: boolean;
}

type Props = ActivityAssignmentStepProps;

const ActivityAssignmentStep = ({
  activityAssignment,
  stepWithResponse,
  stepNumber,
  totalSteps,
  hideComplete,
}: Props) => {
  const { ability, user, userTenantId } = useAuthContext();
  const { step, response } = stepWithResponse;
  const { mediaGalleryData } = useActivityAssignmentStepDetails(stepWithResponse);
  const hasMedia =
    (stepWithResponse.response?.media && !!stepWithResponse.response?.media.length) ||
    (stepWithResponse.step.media && !!stepWithResponse.step.media.length);
  const question = step.question;

  const hasCompletePermissions = () =>
    hasPermissions(ability, 'complete', 'Step', { tenantId: userTenantId }) ||
    hasPermissions(ability, 'complete', 'Step', { stepId: stepWithResponse.step.id }) ||
    hasPermissions(ability, 'complete', 'Step', {
      userId: user?.user?.id,
    }) ||
    hasPermissions(ability, 'complete', 'StepResponse', { stepResponseId: stepWithResponse.id });

  return (
    <Grid
      item
      id={`step-${step.id}`}
      component={Paper}
      mt={stepNumber !== 1 ? 2 : 0}
      mr={2}
      padding='1.5rem'
      width='100%'
      borderRadius='0.75rem'
      elevation={0}
    >
      <Grid container mb={2}>
        <Grid item xs={12}>
          <SectionHeader
            containerPadding='0'
            bold
            alignItems='flex-start'
            title={
              <>
                <Typography sx={{ marginBottom: '2rem' }}>
                  Step {stepNumber} of {totalSteps}
                </Typography>
                <Typography variant='h3' component='span' sx={{ fontWeight: 400, color: 'black' }}>
                  {step.subject}
                </Typography>
              </>
            }
            actions={
              <Box sx={{ position: 'absolute', top: '0', right: '0' }}>
                {step.requiresReview && (
                  <Tag
                    text='Review Required'
                    color={themeOptions.colors.warning}
                    fontWeight={500}
                    padding='0.25rem 0.625rem'
                  />
                )}
                &nbsp;
                {step.isRequired && (
                  <Tag
                    text='Step Required'
                    color={themeOptions.colors.error900}
                    margin='0 0 0 0.75rem'
                    fontWeight={500}
                    padding='0.25rem 0.625rem'
                  />
                )}
              </Box>
            }
          />
        </Grid>
      </Grid>
      <Grid container mt={3}>
        <Div display='flex' width='100%' flexWrap='wrap'>
          <RowDetail title='Assigned To' width='7rem' marginRight={question ? '6.4rem' : '7.2rem'}>
            {getUsersName(
              stepWithResponse.response?.assignedUser || stepWithResponse.step.assignedUser
            )}
          </RowDetail>
          {question ? (
            <>
              <RowDetail
                title='Start Date'
                value={formatDatePickerDate(step.startDate)}
                width='7rem'
                marginRight='6.4rem'
              />
              <RowDetail
                title='Due Date'
                value={formatDatePickerDate(step.dueDate)}
                width='calc(100% - 26.8rem)'
                minWidth='7rem'
              />
            </>
          ) : (
            <RowDetail
              title='Description'
              value={stepWithResponse.step.description}
              width='calc(100% - 14.2rem)'
              minWidth='17rem'
              richText
            />
          )}
        </Div>
        <OverviewDetail title='Media' hide={!hasMedia}>
          <MediaGallery files={mediaGalleryData} sm={4} md={6} lg={4} xl={3} />
        </OverviewDetail>
        {question && (
          <StepQuestion
            activityAssignment={activityAssignment}
            stepWithResponse={stepWithResponse}
          />
        )}
        {!question && (
          <Div display='flex' width='100%' flexWrap='wrap'>
            <RowDetail
              title='Start Date'
              value={formatDatePickerDate(step.startDate)}
              width='7rem'
              marginRight='7.2rem'
            />
            <RowDetail
              title='Estimated Time to Complete'
              value={formatMinutes(step.estimatedTimeToComplete)}
              width='calc(100% - 14.2rem)'
              minWidth='17rem'
            />
            <RowDetail
              title='Due Date'
              value={formatDatePickerDate(step.dueDate)}
              width='7rem'
              marginRight='7.2rem'
            />
            <RowDetail
              title='Actual Time to Complete'
              value={formatMinutes(response?.actualTimeToComplete)}
              width='calc(100% - 14.2rem)'
              minWidth='17rem'
            />
          </Div>
        )}
      </Grid>
      <Grid item pt={5}>
        <Stack direction='row' alignItems='center'>
          {!hideComplete && !response?.completedDate && !question && (
            <CompleteAssignmentStep
              activityAssignment={activityAssignment}
              stepWithResponse={stepWithResponse}
              stepNumber={stepNumber}
              totalSteps={totalSteps}
            />
          )}
          {response?.completedDate && (
            <CustomIcon
              label={
                response?.completedByUser
                  ? `Completed by ${getUsersName(response.completedByUser)} on ${dateFormatter(
                      response.completedDate
                    )}`
                  : `Completed on ${dateFormatter(response.completedDate)}`
              }
              icon={<CheckIcon />}
              color={themeOptions.colors.success900}
            />
          )}
          {hasCompletePermissions() && (
            <>
              {!response?.completedDate && question && (
                <Typography>Provide an answer to complete this step</Typography>
              )}
              <Box ml={2}>
                <DropdownMenu
                  buttonProps={{
                    variant: 'outlined',
                    size: 'small',
                    sx: {
                      paddingLeft: 0,
                      paddingRight: 0,
                      minWidth: '1.875rem',
                      '& > svg': { fontSize: '1.25rem' },
                    },
                  }}
                  items={[
                    {
                      label: (
                        <CommentOnAssignmentStep
                          activityAssignment={activityAssignment}
                          stepWithResponse={stepWithResponse}
                          text='Comment on Step'
                        />
                      ),
                    },
                  ]}
                />
              </Box>
            </>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ActivityAssignmentStep;
